import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { aiResourcesData } from '../data/aiResources';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiX } = FiIcons;

const SearchBar = ({ searchTerm, onSearchChange, onCategoryReset }) => {
  const { t, language } = useLanguage();
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const suggestions = ['ChatGPT', 'Audio', 'Música', 'Video', 'Miro'].sort();

  // Generate autocomplete suggestions based on search term
  const autocompleteSuggestions = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return [];

    const searchLower = searchTerm.toLowerCase();
    const results = new Set();

    // Search in resource names (highest priority)
    aiResourcesData.forEach(resource => {
      if (resource.name.toLowerCase().includes(searchLower)) {
        results.add({
          text: resource.name,
          type: 'resource',
          category: resource.category
        });
      }
    });

    // Search in tags
    aiResourcesData.forEach(resource => {
      const getTags = () => {
        if (resource.tags && typeof resource.tags === 'object') {
          return resource.tags[language] || resource.tags['es'] || [];
        }
        return resource.tags || [];
      };

      const tags = getTags();
      tags.forEach(tag => {
        if (tag.toLowerCase().includes(searchLower) && !Array.from(results).some(r => r.text === tag)) {
          results.add({
            text: tag,
            type: 'tag',
            category: resource.category
          });
        }
      });
    });

    // Search in categories
    const categoryMap = {
      'Investigación': t('investigacion'),
      'Texto y redacción': t('texto-redaccion'),
      'Educación': t('educacion'),
      'Chatbots': t('chatbots'),
      'Video': t('video'),
      'Mapas mentales': t('mapas-mentales'),
      'Imágenes': t('imagenes'),
      'Diseño': t('diseno'),
      'Generador de asistentes virtuales': t('generador-asistentes'),
      'Detector de contenidos de IA': t('detector-contenidos'),
      'Música': t('musica'),
      'Motores de búsqueda': t('motores-busqueda'),
      'Audio': t('audio'),
      'Redes sociales': t('redes-sociales')
    };

    Object.entries(categoryMap).forEach(([originalCategory, translatedCategory]) => {
      if (translatedCategory.toLowerCase().includes(searchLower) && !Array.from(results).some(r => r.text === translatedCategory)) {
        results.add({
          text: translatedCategory,
          type: 'category',
          category: originalCategory
        });
      }
    });

    // Convert to array, sort by relevance, and limit results
    return Array.from(results)
      .sort((a, b) => {
        // Prioritize exact matches
        const aExact = a.text.toLowerCase() === searchLower;
        const bExact = b.text.toLowerCase() === searchLower;
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;

        // Then prioritize starts with
        const aStarts = a.text.toLowerCase().startsWith(searchLower);
        const bStarts = b.text.toLowerCase().startsWith(searchLower);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;

        // Then by type priority (resource > tag > category)
        const typePriority = { resource: 0, tag: 1, category: 2 };
        const aPriority = typePriority[a.type] || 3;
        const bPriority = typePriority[b.type] || 3;
        if (aPriority !== bPriority) return aPriority - bPriority;

        // Finally alphabetically
        return a.text.localeCompare(b.text);
      })
      .slice(0, 6);
  }, [searchTerm, language, t]);

  const handleSuggestionClick = (suggestion) => {
    onSearchChange(suggestion);
    // Reset category to "all" when user selects a suggestion
    if (onCategoryReset) {
      onCategoryReset();
    }
    setShowAutocomplete(false);
    setSelectedIndex(-1);
  };

  const handleClearSearch = () => {
    onSearchChange('');
    setShowAutocomplete(false);
    setSelectedIndex(-1);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    onSearchChange(value);
    
    // Reset category to "all" when user types in search
    if (value && onCategoryReset) {
      onCategoryReset();
    }
    
    setShowAutocomplete(value.length >= 2);
    setSelectedIndex(-1);
  };

  const handleInputFocus = () => {
    if (searchTerm.length >= 2) {
      setShowAutocomplete(true);
    }
  };

  const handleInputBlur = (e) => {
    // Delay hiding to allow clicking on suggestions
    setTimeout(() => {
      if (!autocompleteRef.current?.contains(e.relatedTarget)) {
        setShowAutocomplete(false);
        setSelectedIndex(-1);
      }
    }, 150);
  };

  const handleKeyDown = (e) => {
    if (!showAutocomplete || autocompleteSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < autocompleteSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : autocompleteSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < autocompleteSuggestions.length) {
          handleSuggestionClick(autocompleteSuggestions[selectedIndex].text);
        }
        break;
      case 'Escape':
        setShowAutocomplete(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target)
      ) {
        setShowAutocomplete(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getCategoryEmoji = (category) => {
    const emojiMap = {
      'Audio': '🎙️',
      'Chatbots': '🤖',
      'Detector de contenidos de IA': '🔎',
      'Diseño': '🎨',
      'Educación': '🎓',
      'Generador de asistentes virtuales': '🗣️',
      'Imágenes': '🖼️',
      'Investigación': '🔬',
      'Mapas mentales': '🧠',
      'Música': '🎵',
      'Motores de búsqueda': '🔎',
      'Redes sociales': '📱',
      'Texto y redacción': '📝',
      'Video': '🎬'
    };
    return emojiMap[category] || '🤖';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'resource': return '🔗';
      case 'tag': return '🏷️';
      case 'category': return '📂';
      default: return '🔍';
    }
  };

  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-900/50 text-current px-1 rounded">$1</mark>');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative max-w-2xl mx-auto mb-8"
    >
      <div className="relative">
        <SafeIcon
          icon={FiSearch}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
        />
        <input
          ref={inputRef}
          type="text"
          placeholder={t('buscar-herramientas')}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          className="search-input w-full pl-12 pr-12 py-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-lg"
          autoComplete="off"
        />
        {searchTerm && (
          <button
            onClick={handleClearSearch}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        )}

        {/* Autocomplete Dropdown */}
        <AnimatePresence>
          {showAutocomplete && autocompleteSuggestions.length > 0 && (
            <motion.div
              ref={autocompleteRef}
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-80 overflow-y-auto"
            >
              <div className="p-2">
                {autocompleteSuggestions.map((suggestion, index) => (
                  <button
                    key={`${suggestion.text}-${index}`}
                    onClick={() => handleSuggestionClick(suggestion.text)}
                    className={`autocomplete-item w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all flex items-center justify-between group ${
                      selectedIndex === index
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <span className="text-sm">{getTypeIcon(suggestion.type)}</span>
                      <span
                        className="text-sm truncate"
                        dangerouslySetInnerHTML={{
                          __html: highlightMatch(suggestion.text, searchTerm)
                        }}
                      />
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <span className="text-xs text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300 capitalize">
                        {suggestion.type === 'resource' ? 'recurso' : 
                         suggestion.type === 'tag' ? 'etiqueta' : 'categoría'}
                      </span>
                      <span className="text-sm">{getCategoryEmoji(suggestion.category)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Search suggestions */}
      <div className="mt-4">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 text-center">
          {t('sugerencias-busqueda')}
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => handleSuggestionClick(suggestion)}
              className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default SearchBar;